import cx from 'clsx';
import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { getLanguageDisplayName, getCommonString } from '../../utils/i18n';
import styles from './SearchFilters.module.scss';

import { locales } from '../../../i18n';

export function FilterCard({ filterName, filterConfig, onFilterSelected }) {
  const { label, options } = filterConfig;
  return (
    <div data-testid="FilterCard" className={styles.filterCard}>
      <div className={styles.filterName}>{label}</div>
      <ul className={styles.searchFilterList}>
        {options.map(({ id, label: optionLabel, selected }) => (
          <li key={id} className={styles.searchFilterItem}>
            <FilterEntry
              filter={filterName}
              value={id}
              selected={selected}
              label={optionLabel}
              onSelect={onFilterSelected}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
FilterCard.propTypes = {
  filterName: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filterConfig: PropTypes.object.isRequired,
  onFilterSelected: PropTypes.func.isRequired
};

/* eslint-disable jsx-a11y/label-has-associated-control */
// I couldn't figure out how to put this right next to where it happens in JSX - the normal workaround refused to work
function FilterEntry({ filter, value, selected, label, onSelect }) {
  return (
    <>
      {/* There's clearly an <input> inside the <label> but for some reason eslint still trips up */}
      <label className={styles.searchFilterLabel}>
        <input
          type="checkbox"
          className={styles.searchFilterCheckbox}
          checked={selected || false}
          onChange={() => onSelect(filter, value, !selected)}
        />
        <span className={styles.replacementCheckbox} />
        <span
          className={cx({
            [styles.searchFilterText]: true,
            [styles.searchFilterTextSelected]: selected
          })}>
          {label}
        </span>
      </label>
    </>
  );
}

FilterEntry.propTypes = {
  filter: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired
};

FilterEntry.defaultProps = {
  selected: false
};

function getLanguageFilter(t, lang) {
  return {
    id: 'language',
    onlyOne: true,
    options: locales.reduce((acc, current) => {
      acc.push({
        id: current,
        label: getLanguageDisplayName(current, t),
        selected: current === lang
      });
      return acc;
    }, [])
  };
}

function translateFilter(source, t) {
  const filter = cloneDeep(source);
  if (filter.id && !filter.label) {
    filter.label = getCommonString(`searchFilter-${filter.id}`, t);
  }

  if (filter.options && filter.options.length) {
    filter.options = filter.options.map((initial) => {
      const option = cloneDeep(initial);
      if (option.id && !option.label) {
        option.label = getCommonString(`searchFilterOption-${option.id}`, t);
      }
      return option;
    });
  }

  return filter;
}
export function getInitialFilters(t, lang) {
  return {
    content_type: translateFilter(
      {
        id: 'content_type',
        options: [{ id: 'topic' }, { id: 'sgc' }, { id: 'live' }]
      },
      t
    ),
    role: translateFilter(
      {
        id: 'role',
        options: [
          { id: 'role_admin' },
          { id: 'role_team_member' },
          { id: 'role_fundamentals' },
          { id: 'role_help_desk' }
        ]
      },
      t
    ),
    language: translateFilter(getLanguageFilter(t, lang), t)
  };
}

export function reduceFilters(filters, filterName, optionName, value) {
  if (filters[filterName] && filters[filterName].options.length) {
    let { options } = filters[filterName];
    if (filters[filterName].onlyOne) {
      options = options.map((option) => {
        return {
          ...option,
          selected: false
        };
      });
    }

    options = options.map((option) => {
      if (option.id === optionName) {
        return {
          ...option,
          selected: value
        };
      }

      return option;
    });

    return {
      ...filters,
      [filterName]: {
        ...filters[filterName],
        options: [...options]
      }
    };
  }

  return filters;
}

function getSelectedFilters(filters) {
  const ret = {};
  Object.entries(filters).forEach((filter) => {
    const [filterName, filterConfig] = filter;
    const selectedOptions = Object.entries(filterConfig.options)
      .filter((option) => {
        const [_optionName, props] = option;
        return props.selected;
      })
      .map((option) => {
        const [_optionName, props] = option;
        return props.id;
      });

    if (selectedOptions.length) {
      ret[filterName] = selectedOptions;
    }
  });

  return ret;
}

const filterNameMap = {
  role: 'edu_tags',
  content_type: 'edu_type',
  language: 'edu_lang'
};

const filterValueMap = {
  topic: 'pageCourseTopic',
  sgc: 'pageCourse',
  live: 'pageLiveCourse'
};

export function translateFiltersToQuery(filters) {
  const selected = getSelectedFilters(filters);
  return Object.keys(selected)
    .reduce((acc, key) => {
      const [filterName, selectedValues] = [key, selected[key]];
      if (!filterName || !selectedValues || !selectedValues.length) {
        return acc;
      }
      const selectedString = selectedValues
        .reduce((a, value) => {
          a.push(`"${filterValueMap[value] || value}"`);
          return a;
        }, [])
        .join(', ');

      acc.push(`(@${filterNameMap[filterName] || filterName}==(${selectedString}))`);
      return acc;
    }, [])
    .join(' ');
}
